import React, { Component } from "react";
import {
  withStyles,
  Box,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Grid,
  Typography,
  TextField,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  Chip,
  Divider,
  Tooltip,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import { TablePagination } from "@material-ui/core";
import axios from "axios";
import { connect } from "react-redux";
import { URL } from "../../config/config";
import MarkdownPreview from "./MarkdownPreview";

const primaryTabLabels = [
  { label: "Vendors", key: "vendors" },
  { label: "App Requests", key: "appRequests" },
  { label: "Published apps", key: "privateApps" },
  { label: "App Sequence", key: "appSequence" },
];

const secondaryTabLabels = ["Submissions", "Approved", "Rejected"];

// Vendors: secondary tab index -> API status
const VENDORS_STATUS_MAP = {
  0: "requesting", // Submissions
  1: "approved",
  2: "rejected",
};

// App Requests: secondary tab index -> API status
const APP_REQUESTS_STATUS_MAP = {
  0: "RFA",    // Submissions (Request For Approval)
  1: "APPROVED",
  2: "REJECTED",
  3: "PENDING",
};

class MarketplacePage extends Component {
  state = {
    primaryTab: 0,
    secondaryTab: 0,
    search: "",
    page: 0,
    rowsPerPage: 25,
    data: [],
    isLoading: false,
    totalCount: 0,
    reviewLoadingId: null,
    snackbar: { open: false, message: "", severity: "info" },
    rejectDialogOpen: false,
    rejectReason: "",
    rejectTarget: null,
    togglingId: null,
    detailDialogOpen: false,
    detailRow: null,
    detailType: "appRequest",
    isEditingSequence: false,
    sequenceDraft: [],
    savingSequence: false,
    dragIndex: null,
    dragOverIndex: null,
  };

  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps, prevState) {
    const { primaryTab, secondaryTab } = this.state;
    if (
      primaryTab !== prevState.primaryTab ||
      secondaryTab !== prevState.secondaryTab
    ) {
      this.cancelSequenceEdit();
      this.fetchData();
    }
  }

  // `ordered-apps` returns appStoreApp docs keyed by `_id` — that is the id the
  // reorder endpoint expects in `orderedAppIds`.
  getAppId = (row) => row?._id ?? row?.id ?? row?.appStoreAppId;

  fetchData = () => {
    const { primaryTab, secondaryTab, search } = this.state;
    const token = localStorage.getItem("session");

    const params = new URLSearchParams();
    if (search?.trim()) params.append("search", search.trim());

    let endpoint;
    let dataKey;
    if (primaryTab === 0) {
      const status = VENDORS_STATUS_MAP[secondaryTab] ?? "requesting";
      params.append("status", status);
      endpoint = `${URL}/superadmin/vendors`;
      dataKey = "vendors";
    } else if (primaryTab === 1) {
      const status = APP_REQUESTS_STATUS_MAP[secondaryTab] ?? "RFA";
      params.append("status", status);
      endpoint = `${URL}/superadmin/private-app/versions`;
      dataKey = "appVersions";
    } else if (primaryTab === 2) {
      endpoint = `${URL}/superadmin/private-app/vendor-apps`;
      dataKey = "apps";
    } else {
      endpoint = `${URL}/superadmin/private-app/ordered-apps`;
      dataKey = "appStoreApps";
    }

    this.setState({ isLoading: true });
    axios
      .get(`${endpoint}?${params.toString()}`)
      .then((res) => {
        const list =
          res?.data?.[dataKey] ??
          res?.data?.apps ??
          res?.data?.vendorApps ??
          res?.data?.appStoreApps ??
          res?.data ??
          [];
        let items = Array.isArray(list) ? list : [];
        if (primaryTab === 3) {
          // App Sequence: lowest `order` first, stable for ties.
          items = [...items].sort(
            (a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER)
          );
        }
        if (primaryTab === 0 && secondaryTab === 0) {
          items = items.filter((v) => {
            const approved = v.approval?.approved ?? v.approved;
            return approved !== true && approved !== false;
          });
        }
        this.setState({
          data: items,
          totalCount: items.length,
          isLoading: false,
        });
      })
      .catch((err) => {
        const label =
          primaryTab === 0
            ? "vendors"
            : primaryTab === 1
              ? "app requests"
              : primaryTab === 2
                ? "private apps"
                : "ordered apps";
        console.error(`Failed to fetch ${label}:`, err);
        this.setState({
          data: [],
          totalCount: 0,
          isLoading: false,
        });
      });
  };

  handlePrimaryTabChange = (_, v) => {
    this.setState({ primaryTab: v, secondaryTab: 0, page: 0 });
  };

  handleSecondaryTabChange = (_, v) => {
    this.setState({ secondaryTab: v, page: 0 });
  };

  handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      // A new result set invalidates any in-progress reorder.
      this.cancelSequenceEdit();
      this.setState({ page: 0 }, () => this.fetchData());
    }
  };

  handleChangePage = (_, newPage) => {
    this.setState({ page: newPage });
  };

  reviewVendor = (vendorId, approved) => {
    this.reviewItem(vendorId, approved, false);
  };

  reviewAppRequest = (row, approved) => {
    const versionId = row._id ?? row.id;
    const vendorId = row.vendorId ?? row.vendor_id;
    this.reviewItem(versionId, approved, true, vendorId);
  };

  openRejectDialog = (row, isAppRequest) => {
    this.setState({
      rejectDialogOpen: true,
      rejectReason: "",
      rejectTarget: { row, isAppRequest },
    });
  };

  closeRejectDialog = () => {
    this.setState({
      rejectDialogOpen: false,
      rejectReason: "",
      rejectTarget: null,
    });
  };

  openDetailDialog = (row, type = "appRequest") => {
    this.setState({ detailDialogOpen: true, detailRow: row, detailType: type });
  };

  closeDetailDialog = () => {
    this.setState({ detailDialogOpen: false, detailRow: null });
  };

  confirmReject = () => {
    if (!this.hasWriteAccess()) return;
    const { rejectTarget, rejectReason } = this.state;
    if (!rejectTarget || !rejectReason.trim()) return;
    const { row, isAppRequest } = rejectTarget;
    if (isAppRequest) {
      const versionId = row._id ?? row.id;
      const vendorId = row.vendorId ?? row.vendor_id;
      this.reviewItem(versionId, false, true, vendorId, rejectReason.trim());
    } else {
      this.reviewItem(row._id ?? row.id, false, false, undefined, rejectReason.trim());
    }
    this.closeRejectDialog();
  };

  hasWriteAccess = () => {
    const { permissions } = this.props;
    const marketplacePermission = permissions?.find(
      (p) => p.label === "marketplace"
    );
    return !!marketplacePermission?.write;
  };

  reviewItem = (id, approved, isAppRequest, vendorIdForAppRequest, rejectReason) => {
    if (!this.hasWriteAccess()) return;
    const token = localStorage.getItem("session");
    const body = isAppRequest
      ? { versionId: id, approved, vendorId: vendorIdForAppRequest }
      : { vendorId: id, approved };
    if (!approved && rejectReason) body.rejectReason = rejectReason;
    const label = isAppRequest ? "App request" : "Vendor";

    const endpoint = isAppRequest
      ? `${URL}/superadmin/private-app/review-version`
      : `${URL}/superadmin/vendor/review`;

    this.setState({ reviewLoadingId: id });
    axios
      .post(endpoint, body)
      .then(() => {
        this.setState({
          reviewLoadingId: null,
          snackbar: {
            open: true,
            message: approved
              ? `${label} approved`
              : `${label} rejected`,
            severity: "success",
          },
        });
        this.fetchData();
      })
      .catch((err) => {
        console.error(`Failed to review ${label}:`, err);
        this.setState({
          reviewLoadingId: null,
          snackbar: {
            open: true,
            message: err?.response?.data?.message ?? `Failed to update ${label}`,
            severity: "error",
          },
        });
      });
  };

  togglePrivateAppVisibility = (row) => {
    if (!this.hasWriteAccess()) return;
    const appStoreAppId = row.appStoreAppId ?? row._id ?? row.id;
    if (!appStoreAppId) return;
    const nextHidden = !row.hidden;

    this.setState((prev) => ({
      togglingId: appStoreAppId,
      data: prev.data.map((item) => {
        const itemId = item.appStoreAppId ?? item._id ?? item.id;
        return itemId === appStoreAppId ? { ...item, hidden: nextHidden } : item;
      }),
    }));

    axios
      .post(`${URL}/superadmin/private-app/toggle-visibility`, {
        appStoreAppId,
        hidden: nextHidden,
      })
      .then(() => {
        this.setState({
          togglingId: null,
          snackbar: {
            open: true,
            message: nextHidden ? "App hidden" : "App unhidden",
            severity: "success",
          },
        });
      })
      .catch((err) => {
        console.error("Failed to toggle app visibility:", err);
        this.setState((prev) => ({
          togglingId: null,
          data: prev.data.map((item) => {
            const itemId = item.appStoreAppId ?? item._id ?? item.id;
            return itemId === appStoreAppId
              ? { ...item, hidden: !nextHidden }
              : item;
          }),
          snackbar: {
            open: true,
            message:
              err?.response?.data?.message ?? "Failed to update app visibility",
            severity: "error",
          },
        }));
      });
  };

  startSequenceEdit = () => {
    if (!this.hasWriteAccess()) return;
    this.setState((prev) => ({
      isEditingSequence: true,
      sequenceDraft: [...prev.data],
      dragIndex: null,
      dragOverIndex: null,
    }));
  };

  cancelSequenceEdit = () => {
    this.setState({
      isEditingSequence: false,
      sequenceDraft: [],
      dragIndex: null,
      dragOverIndex: null,
    });
  };

  handleRowDragStart = (index) => (e) => {
    // Firefox needs data set on the transfer for a drag to start at all.
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", `${index}`);
    } catch (err) {
      /* not supported everywhere, drag still works via state */
    }
    this.setState({ dragIndex: index, dragOverIndex: index });
  };

  handleRowDragOver = (index) => (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (this.state.dragOverIndex !== index) {
      this.setState({ dragOverIndex: index });
    }
  };

  handleRowDrop = (index) => (e) => {
    e.preventDefault();
    const { dragIndex } = this.state;
    if (dragIndex === null || dragIndex === index) {
      this.setState({ dragIndex: null, dragOverIndex: null });
      return;
    }
    this.setState((prev) => {
      const next = [...prev.sequenceDraft];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(index, 0, moved);
      return { sequenceDraft: next, dragIndex: null, dragOverIndex: null };
    });
  };

  handleRowDragEnd = () => {
    this.setState({ dragIndex: null, dragOverIndex: null });
  };

  saveSequence = () => {
    if (!this.hasWriteAccess()) return;
    const { sequenceDraft } = this.state;
    const orderedAppIds = sequenceDraft
      .map((row) => this.getAppId(row))
      .filter(Boolean);

    if (!orderedAppIds.length) {
      this.cancelSequenceEdit();
      return;
    }

    this.setState({ savingSequence: true });
    axios
      .post(`${URL}/superadmin/private-app/reorder`, { orderedAppIds })
      .then(() => {
        this.setState({
          savingSequence: false,
          isEditingSequence: false,
          sequenceDraft: [],
          dragIndex: null,
          dragOverIndex: null,
          snackbar: {
            open: true,
            message: "App sequence updated",
            severity: "success",
          },
        });
        this.fetchData();
      })
      .catch((err) => {
        console.error("Failed to update app sequence:", err);
        this.setState({
          savingSequence: false,
          snackbar: {
            open: true,
            message:
              err?.response?.data?.message ?? "Failed to update app sequence",
            severity: "error",
          },
        });
      });
  };

  closeSnackbar = () => {
    this.setState((s) => ({
      snackbar: { ...s.snackbar, open: false },
    }));
  };

  getPageRows = () => {
    const { data, page, rowsPerPage, primaryTab, isEditingSequence, sequenceDraft } =
      this.state;
    if (isEditingSequence) return sequenceDraft;
    // App Sequence is unpaginated: the order only reads correctly as one list,
    // and rows must be draggable across the whole set.
    if (primaryTab === 3) return data;
    return data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  render() {
    const { classes } = this.props;
    const {
      primaryTab,
      secondaryTab,
      search,
      page,
      rowsPerPage,
      isLoading,
      totalCount,
      data,
      reviewLoadingId,
      snackbar,
      rejectDialogOpen,
      rejectReason,
      detailDialogOpen,
      detailRow,
      detailType,
      isEditingSequence,
      savingSequence,
      dragIndex,
      dragOverIndex,
    } = this.state;
    const isPrivateApps = primaryTab === 2;
    const isAppSequence = primaryTab === 3;
    const isAppRequests = primaryTab === 1;
    const isReviewTab = !isPrivateApps && !isAppSequence;
    const showVendorActions = primaryTab === 0 && secondaryTab === 0;
    const showAppRequestActions = isAppRequests && secondaryTab === 0;
    const showActions = showVendorActions || showAppRequestActions;
    const showRejectReason = isReviewTab && secondaryTab === 2;
    // App Requests always get an ACTION column for the "View" details button.
    const showActionColumn = showActions || isAppRequests;
    const { togglingId } = this.state;
    const hasWriteAccess = this.hasWriteAccess();
    const displayData = this.getPageRows();
    const count = totalCount > 0 ? totalCount : data.length;
    let colSpan = 4;
    if (isPrivateApps) colSpan = 6;
    else if (isAppSequence) colSpan = 5;
    if (isReviewTab && showRejectReason) colSpan += 1;
    if (isReviewTab && showActionColumn) colSpan += 1;

    return (
      <div className={classes.root}>
        {/* Page header: title, search, Report button */}
        <Grid
          container
          alignItems="center"
          className={`${classes.pageTitleContainer} ${classes.fullWidth}`}
          spacing={2}
        >
          <Grid item>
            <Box pl={{ xs: 2, md: 3 }}>
              <Typography variant="h3">Marketplace</Typography>
            </Box>
          </Grid>
          <Grid item>
            <TextField
              name="search"
              placeholder="Search marketplace"
              className={classes.textField}
              value={search}
              onChange={(e) => this.setState({ search: e.target.value })}
              onKeyPress={this.handleSearchKeyPress}
            />
          </Grid>
          <Grid item>
            <Button color="primary" variant="contained">
              Report
            </Button>
          </Grid>
        </Grid>

        {/* Layout: vertical primary tabs + main content */}
        <Grid container style={{ position: "relative", top: "15px" }}>
          {/* Primary vertical tabs (AISENSY, PARTNERS, INDIAMART) */}
          <Grid
            item
            xs={2}
            lg={1}
            xl={1}
            md={2}
            className={classes.tabroot}
          >
            <Tabs
              orientation="vertical"
              variant="scrollable"
              value={primaryTab}
              onChange={this.handlePrimaryTabChange}
              classes={{ indicator: classes.indicator }}
            >
              {primaryTabLabels.map((tab, idx) => (
                <Tab
                  key={tab.key}
                  label={tab.label}
                  style={{
                    background:
                      primaryTab === idx
                        ? "rgba(237, 226, 252, 0.37)"
                        : "white",
                  }}
                />
              ))}
            </Tabs>
          </Grid>

          {/* Main content: secondary tabs + table */}
          <Grid item xs={10} md={10} lg={11} xl={11}>
            <Grid container justify="center">
              <Grid item xs={12} md={11} lg={10}>
                <div className={classes.tableContainer}>
                  {/* Secondary horizontal tabs (PENDING, SUBMITTED, etc.) */}
                  {isReviewTab && (
                    <Tabs
                      value={secondaryTab}
                      onChange={this.handleSecondaryTabChange}
                      indicatorColor="primary"
                      textColor="primary"
                      variant="fullWidth"
                    >
                      {secondaryTabLabels.map((label) => (
                        <Tab key={label} label={label} />
                      ))}
                    </Tabs>
                  )}

                  {/* App Sequence: enter/leave reorder mode */}
                  {isAppSequence && (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-end"
                      pt={1}
                    >
                      {isEditingSequence ? (
                        <>
                          <Typography
                            variant="body2"
                            style={{ color: "#6b7280", marginRight: "auto" }}
                          >
                            Drag rows to reorder, then click Update.
                          </Typography>
                          <Button
                            size="small"
                            onClick={this.cancelSequenceEdit}
                            disabled={savingSequence}
                            style={{ textTransform: "none", marginRight: 8 }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            color="primary"
                            variant="contained"
                            disableElevation
                            onClick={this.saveSequence}
                            disabled={savingSequence}
                            style={{ textTransform: "none", minWidth: 88 }}
                          >
                            {savingSequence ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              "Update"
                            )}
                          </Button>
                        </>
                      ) : (
                        <Button
                          size="small"
                          color="primary"
                          variant="outlined"
                          onClick={this.startSequenceEdit}
                          disabled={!hasWriteAccess || isLoading || !data.length}
                          style={{ textTransform: "none" }}
                        >
                          Edit
                        </Button>
                      )}
                    </Box>
                  )}

                  {/* Data table */}
                  <Box mt={1} flex={1} minHeight={0} display="flex">
                    <TableContainer
                      component={Paper}
                      className={classes.tableScroll}
                    >
                      <Table stickyHeader>
                        <TableHead className={classes.tableHead}>
                          <TableRow>
                            {isPrivateApps ? (
                              <>
                                <TableCell align="left">
                                  <strong>APP ID</strong>
                                </TableCell>
                                <TableCell align="left">
                                  <strong>INSTALL COUNT</strong>
                                </TableCell>
                                <TableCell align="left">
                                  <strong>UNINSTALL COUNT</strong>
                                </TableCell>
                                <TableCell align="left">
                                  <strong>CREATED AT</strong>
                                </TableCell>
                                <TableCell align="left">
                                  <strong>APP NAME</strong>
                                </TableCell>
                                <TableCell align="right">
                                  <strong>VISIBLE</strong>
                                </TableCell>
                              </>
                            ) : isAppSequence ? (
                              <>
                                <TableCell align="left">
                                  <strong>SEQUENCE</strong>
                                </TableCell>
                                <TableCell align="left">
                                  <strong>APP NAME</strong>
                                </TableCell>
                                <TableCell align="left">
                                  <strong>APP ID</strong>
                                </TableCell>
                                <TableCell align="left">
                                  <strong>DESCRIPTION</strong>
                                </TableCell>
                                <TableCell align="right">
                                  <strong>ORDER</strong>
                                </TableCell>
                              </>
                            ) : (
                              <>
                                <TableCell align="left">
                                  <strong>VENDOR ID</strong>
                                </TableCell>
                                <TableCell align="left">
                                  <strong>
                                    {primaryTab === 0 ? "CONTACT" : "VERSION CODE"}
                                  </strong>
                                </TableCell>
                                <TableCell align="left">
                                  <strong>CREATED AT</strong>
                                </TableCell>
                                <TableCell align="left">
                                  <strong>
                                    {primaryTab === 0 ? "VENDOR NAME" : "APP NAME"}
                                  </strong>
                                </TableCell>
                                {showRejectReason && (
                                  <TableCell align="left">
                                    <strong>REJECT REASON</strong>
                                  </TableCell>
                                )}
                                {showActionColumn && (
                                  <TableCell align="right">
                                    <strong>ACTION</strong>
                                  </TableCell>
                                )}
                              </>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {isLoading ? (
                            <TableRow>
                              <TableCell
                                colSpan={colSpan}
                                align="center"
                                style={{ padding: 40 }}
                              >
                                <CircularProgress color="primary" />
                              </TableCell>
                            </TableRow>
                          ) : displayData.length === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={colSpan}
                                align="center"
                                style={{ padding: 40 }}
                              >
                                <Typography color="textSecondary">
                                  There is no data
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : isPrivateApps ? (
                            displayData.map((row, idx) => {
                              const appId =
                                row.appStoreAppId ?? row._id ?? row.id ?? idx;
                              return (
                                <TableRow key={appId}>
                                  <TableCell align="left">
                                    {row.appStoreAppId ?? row._id ?? "-"}
                                  </TableCell>
                                  <TableCell align="left">
                                    {row.installCount ??
                                      row.installsCount ??
                                      row.installs ??
                                      0}
                                  </TableCell>
                                  <TableCell align="left">
                                    {row.uninstallCount ??
                                      row.uninstallsCount ??
                                      row.uninstalls ??
                                      0}
                                  </TableCell>
                                  <TableCell align="left">
                                    {row.createdAt
                                      ? new Date(row.createdAt).toLocaleDateString()
                                      : "-"}
                                  </TableCell>
                                  <TableCell align="left">
                                    {row.displayName ??
                                      row.appName ??
                                      row.name ??
                                      "-"}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Switch
                                      checked={!row.hidden}
                                      onChange={() =>
                                        this.togglePrivateAppVisibility(row)
                                      }
                                      disabled={
                                        !hasWriteAccess || togglingId === appId
                                      }
                                      color="primary"
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          ) : isAppSequence ? (
                            displayData.map((row, idx) => {
                              const appId = this.getAppId(row);
                              return (
                                <TableRow
                                  key={appId ?? idx}
                                  draggable={isEditingSequence}
                                  onDragStart={
                                    isEditingSequence
                                      ? this.handleRowDragStart(idx)
                                      : undefined
                                  }
                                  onDragOver={
                                    isEditingSequence
                                      ? this.handleRowDragOver(idx)
                                      : undefined
                                  }
                                  onDrop={
                                    isEditingSequence
                                      ? this.handleRowDrop(idx)
                                      : undefined
                                  }
                                  onDragEnd={
                                    isEditingSequence
                                      ? this.handleRowDragEnd
                                      : undefined
                                  }
                                  style={
                                    isEditingSequence
                                      ? {
                                          cursor: "move",
                                          opacity: dragIndex === idx ? 0.4 : 1,
                                          backgroundColor:
                                            dragOverIndex === idx &&
                                            dragIndex !== idx
                                              ? "rgba(237, 226, 252, 0.6)"
                                              : undefined,
                                        }
                                      : undefined
                                  }
                                >
                                  <TableCell align="left">
                                    {isEditingSequence ? (
                                      <Box display="flex" alignItems="center">
                                        <DragIndicatorIcon
                                          fontSize="small"
                                          style={{
                                            color: "#9ca3af",
                                            marginRight: 8,
                                          }}
                                        />
                                        {idx + 1}
                                      </Box>
                                    ) : (
                                      idx + 1
                                    )}
                                  </TableCell>
                                  <TableCell align="left">
                                    <Box display="flex" alignItems="center">
                                      {row.iconUrl ? (
                                        <img
                                          src={row.iconUrl}
                                          alt=""
                                          className={classes.appIcon}
                                          onError={(e) => {
                                            e.target.style.visibility =
                                              "hidden";
                                          }}
                                        />
                                      ) : (
                                        <span className={classes.appIcon} />
                                      )}
                                      {row.displayName ??
                                        row.appName ??
                                        row.name ??
                                        "-"}
                                    </Box>
                                  </TableCell>
                                  <TableCell align="left">
                                    {appId ?? "-"}
                                  </TableCell>
                                  <TableCell
                                    align="left"
                                    className={classes.descriptionCell}
                                  >
                                    <Tooltip
                                      title={row.shortDescription ?? ""}
                                      arrow
                                      disableHoverListener={
                                        !row.shortDescription
                                      }
                                    >
                                      <span>{row.shortDescription ?? "-"}</span>
                                    </Tooltip>
                                  </TableCell>
                                  <TableCell align="right">
                                    {row.order ?? "-"}
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          ) : (
                            displayData.map((row, idx) => (
                              <TableRow key={row._id || row.id || idx}>
                                <TableCell align="left">
                                  {primaryTab === 0
                                    ? (row._id ?? row.assistantId ?? "-")
                                    : (row.vendorId ?? row._id ?? "-")}
                                </TableCell>
                                <TableCell align="left">
                                  {primaryTab === 0
                                    ? (row.contact ?? "-")
                                    : (row.versionCode ?? "-")}
                                </TableCell>
                                <TableCell align="left">
                                  {row.createdAt
                                    ? new Date(row.createdAt).toLocaleDateString()
                                    : "-"}
                                </TableCell>
                                <TableCell align="left">
                                  {primaryTab === 0
                                    ? (row.vendorName ?? row.name ?? row.assistantName ?? "-")
                                    : (row.displayName ?? row.versionName ?? "-")}
                                </TableCell>
                                {showRejectReason && (
                                  <TableCell align="left">
                                    {row.approval?.rejectReason ??
                                      row.rejectReason ??
                                      "-"}
                                  </TableCell>
                                )}
                                {showActionColumn && (
                                  <TableCell align="right">
                                    {isAppRequests && (
                                      <Button
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        style={{ marginRight: 8 }}
                                        onClick={() => this.openDetailDialog(row)}
                                      >
                                        View
                                      </Button>
                                    )}
                                    {showVendorActions && (
                                      <Button
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                        style={{ marginRight: 8 }}
                                        onClick={() =>
                                          this.openDetailDialog(row, "vendor")
                                        }
                                      >
                                        View
                                      </Button>
                                    )}
                                    {showActions && (
                                      <>
                                        <Button
                                          size="small"
                                          color="primary"
                                          variant="contained"
                                          disabled={!hasWriteAccess || !!reviewLoadingId}
                                          style={{ marginRight: 8 }}
                                          onClick={() =>
                                            showVendorActions
                                              ? this.reviewVendor(row._id ?? row.id, true)
                                              : this.reviewAppRequest(row, true)
                                          }
                                        >
                                          {reviewLoadingId === (row._id ?? row.id) ? (
                                            <CircularProgress size={18} color="inherit" />
                                          ) : (
                                            "Approve"
                                          )}
                                        </Button>
                                        <Button
                                          size="small"
                                          color="primary"
                                          variant="outlined"
                                          disabled={!hasWriteAccess || !!reviewLoadingId}
                                          onClick={() =>
                                            this.openRejectDialog(row, !showVendorActions)
                                          }
                                        >
                                          Reject
                                        </Button>
                                      </>
                                    )}
                                  </TableCell>
                                )}
                              </TableRow>
                            ))
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                </div>
              </Grid>
            </Grid>

            {/* Pagination — App Sequence renders the full list, so it has none */}
            {!isAppSequence && (
              <div className={classes.bottomContainer}>
                <TablePagination
                  component="div"
                  count={count}
                  page={page}
                  onPageChange={this.handleChangePage}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[25]}
                />
              </div>
            )}
          </Grid>
        </Grid>
        <Dialog
          open={rejectDialogOpen}
          onClose={this.closeRejectDialog}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            style: {
              borderRadius: 12,
              padding: 4,
            },
          }}
        >
          <DialogTitle
            disableTypography
            style={{
              padding: "20px 24px 10px",
            }}
          >
            <Typography variant="h5" style={{ fontWeight: 600, color: "#1a1a1a" }}>
              Reject request
            </Typography>
          </DialogTitle>
          <DialogContent style={{ padding: "0 24px 8px" }}>
            <Typography
              variant="body2"
              style={{
                color: "#374151",
                marginBottom: 12,
              }}
            >
              Please provide the reason for rejection
            </Typography>
            <TextField
              autoFocus
              fullWidth
              multiline
              minRows={4}
              maxRows={8}
              variant="outlined"
              placeholder="Enter the reason for rejecting this request"
              value={rejectReason}
              onChange={(e) => this.setState({ rejectReason: e.target.value })}
              inputProps={{ maxLength: 5024 }}
            />
          </DialogContent>
          <DialogActions style={{ padding: "8px 24px 20px" }}>
            <Button
              onClick={this.closeRejectDialog}
              style={{
                textTransform: "none",
                color: "#4b5563",
                fontWeight: 500,
                padding: "6px 16px",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={this.confirmReject}
              variant="contained"
              disableElevation
              disabled={
                !hasWriteAccess || !rejectReason.trim() || !!reviewLoadingId
              }
              style={{
                textTransform: "none",
                fontWeight: 500,
                padding: "6px 20px",
                marginLeft: 8,
                backgroundColor:
                  !hasWriteAccess || !rejectReason.trim() || !!reviewLoadingId
                    ? undefined
                    : "#dc2626",
                color:
                  !hasWriteAccess || !rejectReason.trim() || !!reviewLoadingId
                    ? undefined
                    : "#fff",
              }}
            >
              {reviewLoadingId ? (
                <CircularProgress size={18} style={{ color: "#fff" }} />
              ) : (
                "Reject request"
              )}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={detailDialogOpen}
          onClose={this.closeDetailDialog}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            style: {
              borderRadius: 12,
              padding: 4,
            },
          }}
        >
          <DialogTitle
            disableTypography
            style={{ padding: "20px 24px 10px" }}
          >
            <Typography variant="h5" style={{ fontWeight: 600, color: "#1a1a1a" }}>
              {detailType === "vendor"
                ? detailRow?.vendorName ??
                  detailRow?.name ??
                  detailRow?.assistantName ??
                  "Vendor details"
                : detailRow?.displayName ?? detailRow?.versionName ?? "App request"}
            </Typography>
            {detailType === "vendor" ? (
              <Typography variant="body2" style={{ color: "#6b7280", marginTop: 2 }}>
                Submission details
              </Typography>
            ) : (
              detailRow?.versionCode != null && (
                <Typography variant="body2" style={{ color: "#6b7280", marginTop: 2 }}>
                  Version {detailRow?.versionName ?? "-"} (code {detailRow.versionCode})
                </Typography>
              )
            )}
          </DialogTitle>
          <DialogContent
            className={classes.detailContent}
            style={{ padding: "0 24px 16px" }}
          >
            {detailType === "vendor" && (
              <Box
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  background: "#fafafa",
                  overflow: "hidden",
                }}
              >
                <Typography
                  variant="caption"
                  style={{
                    display: "block",
                    padding: "14px 16px 10px",
                    color: "#9ca3af",
                    fontWeight: 600,
                    letterSpacing: "0.06em",
                  }}
                >
                  SUBMITTED DETAILS
                </Typography>
                {[
                  {
                    label: "Name",
                    value:
                      detailRow?.vendorName ??
                      detailRow?.name ??
                      detailRow?.assistantName,
                  },
                  { label: "Email", value: detailRow?.email },
                  { label: "Contact", value: detailRow?.contact },
                  {
                    label: "Business website",
                    value:
                      detailRow?.businessWebsite ??
                      detailRow?.business_website ??
                      detailRow?.website,
                  },
                  {
                    label: "Number of users",
                    value:
                      detailRow?.numberOfUsers ??
                      detailRow?.noOfUsers ??
                      detailRow?.number_of_users ??
                      detailRow?.users,
                  },
                  {
                    label: "Monthly revenue",
                    value: (() => {
                      const revenue =
                        detailRow?.monthlyRevenue ??
                        detailRow?.monthly_revenue ??
                        detailRow?.revenue;
                      if (revenue == null || `${revenue}`.trim() === "")
                        return revenue;
                      const symbol =
                        detailRow?.revenueCurrency === "USD" ? "$" : "₹";
                      return `${symbol}${revenue}`;
                    })(),
                  },
                ].map((field) => (
                  <Box
                    key={field.label}
                    display="flex"
                    alignItems="center"
                    style={{
                      padding: "12px 16px",
                      borderTop: "1px solid #eef0f2",
                    }}
                  >
                    <Typography
                      variant="body2"
                      style={{
                        width: 120,
                        flexShrink: 0,
                        color: "#6b7280",
                      }}
                    >
                      {field.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      style={{
                        color: "#1a1a1a",
                        fontWeight: 500,
                        wordBreak: "break-word",
                      }}
                    >
                      {field.value != null && `${field.value}`.trim() !== ""
                        ? field.value
                        : "—"}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {detailType !== "vendor" && detailRow?.description && (
              <>
                <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 4 }}>
                  App Tagline
                </Typography>
                <Typography variant="body2" style={{ color: "#374151", marginBottom: 16 }}>
                  {detailRow.description}
                </Typography>
              </>
            )}

            {detailType !== "vendor" &&
              `${detailRow?.listingMarkdown ?? detailRow?.listing_markdown ?? ""}`.trim() !== "" && (
                <>
                  <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                    App Description
                  </Typography>
                  <Box
                    mb={2}
                    p={1.5}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 8,
                      background: "#fafafa",
                    }}
                  >
                    <MarkdownPreview
                      source={detailRow.listingMarkdown ?? detailRow.listing_markdown}
                    />
                  </Box>
                </>
              )}

            {detailType !== "vendor" && (
              <>
            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 4 }}>
              Webhook URL
            </Typography>
            <Typography
              variant="body2"
              style={{ color: "#374151", marginBottom: 16, wordBreak: "break-all" }}
            >
              {detailRow?.webhookUrl?.trim() ? detailRow.webhookUrl : "—"}
            </Typography>

            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
              Webhook Topics
            </Typography>
            <Box mb={2}>
              {detailRow?.webhookTopics?.length ? (
                detailRow.webhookTopics.map((topic, idx) => {
                  const topicName =
                    typeof topic === "string" ? topic : topic?.topic;
                  const topicReason =
                    typeof topic === "string" ? "" : topic?.reason;
                  return (
                    <Tooltip
                      key={topic?._id ?? topicName ?? idx}
                      title={topicReason || ""}
                      arrow
                      disableHoverListener={!topicReason}
                    >
                      <Chip
                        label={topicName}
                        size="small"
                        style={{ marginRight: 6, marginBottom: 6 }}
                      />
                    </Tooltip>
                  );
                })
              ) : (
                <Typography variant="body2" style={{ color: "#9ca3af" }}>
                  None
                </Typography>
              )}
            </Box>

            <Divider style={{ margin: "8px 0 16px" }} />

            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
              Permissions
            </Typography>
            {detailRow?.permissions?.length ? (
              detailRow.permissions.map((perm, idx) => (
                <Box
                  key={perm._id ?? perm.key ?? idx}
                  mb={1.5}
                  p={1.5}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 8,
                    background: "#fafafa",
                  }}
                >
                  <Box display="flex" alignItems="center" style={{ marginBottom: 4 }}>
                    <Typography
                      variant="body2"
                      style={{ fontWeight: 600, color: "#1a1a1a", marginRight: 8 }}
                    >
                      {perm.key}
                    </Typography>
                    <Chip
                      label={perm.mandatory ? "Mandatory" : "Optional"}
                      size="small"
                      style={{
                        height: 20,
                        backgroundColor: perm.mandatory ? "#fee2e2" : "#e5e7eb",
                        color: perm.mandatory ? "#b91c1c" : "#374151",
                      }}
                    />
                  </Box>
                  {perm.reason && (
                    <Typography variant="body2" style={{ color: "#6b7280" }}>
                      Reason: {perm.reason}
                    </Typography>
                  )}
                </Box>
              ))
            ) : (
              <Typography variant="body2" style={{ color: "#9ca3af" }}>
                No permissions requested
              </Typography>
            )}
              </>
            )}
          </DialogContent>
          <DialogActions style={{ padding: "8px 24px 20px" }}>
            <Button
              onClick={this.closeDetailDialog}
              variant="contained"
              color="primary"
              disableElevation
              style={{ textTransform: "none", fontWeight: 500, padding: "6px 20px" }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={this.closeSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={this.closeSnackbar} severity={snackbar.severity}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    );
  }
}

const styles = (theme) => ({
  root: {
    width: "100%",
    height: "100%",
    background: "rgb(249,249,249)",
    boxSizing: "border-box",
    overflowX: "hidden",
    position: "relative",
  },
  pageTitleContainer: {
    position: "sticky",
    zIndex: 100,
    top: 0,
    height: 60,
    boxSizing: "border-box",
  },
  fullWidth: {
    width: "100%",
    background: "white",
  },
  textField: {
    width: 300,
  },
  tabroot: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
  },
  indicator: {
    backgroundColor: "#461486",
  },
  tableContainer: {
    top: "60px",
    height: "calc(100vh - 70px - 60px - 40px)",
    width: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
    position: "sticky",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.down("sm")]: {
      height: "calc(100vh - 70px - 50px - 10px - 50px)",
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      boxSizing: "border-box",
    },
  },
  // The table body scrolls inside the fixed-height page area instead of being
  // clipped by it.
  tableScroll: {
    flex: 1,
    minHeight: 0,
    width: "100%",
    overflow: "auto",
  },
  tableHead: {
    // stickyHeader paints the cells, not the head row, so colour them directly.
    "& th": {
      backgroundColor: "#f0f0f0",
    },
  },
  appIcon: {
    width: 24,
    height: 24,
    flexShrink: 0,
    marginRight: 10,
    objectFit: "contain",
    borderRadius: 4,
  },
  descriptionCell: {
    maxWidth: 320,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#6b7280",
  },
  detailContent: {
    scrollbarWidth: "thin",
    scrollbarColor: "rgba(0,0,0,0.25) transparent",
    "&::-webkit-scrollbar": {
      width: 6,
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "transparent",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(0,0,0,0.25)",
      borderRadius: 3,
    },
  },
  bottomContainer: {
    marginTop: "5px",
    height: "50px",
    background: "white",
    bottom: 0,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    paddingRight: 24,
    borderTop: "1px solid lightgrey",
  },
});

const mapStateToProps = (state) => ({
  permissions: state.login.permissions,
});

export default connect(mapStateToProps)(withStyles(styles)(MarketplacePage));
